if (!customElements.get('qls-shoppable-video')) {
  class QlsShoppableVideo extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('.qls-social__dialog');
      this.dialogVideo = this.querySelector('.qls-social__dialog-video');
      this.dialogImage = this.querySelector('.qls-social__dialog-image');
      this.activeIndex = -1;
      this.preferredMuted = true;
      this.addedProductForms = new Set();
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('pointerover', this.onPointerOver.bind(this));
      this.addEventListener('pointerout', this.onPointerOut.bind(this));
      this.addEventListener('focusin', this.onFocusIn.bind(this));
      this.addEventListener('submit', this.onProductSubmit.bind(this), true);
      this.dialog.addEventListener('close', () => this.stopDialog());
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
    }

    hydrate(video) {
      if (!video || video.dataset.ready === 'true') return;
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.dataset.ready = 'true';
      video.load();
    }

    preview(tile) {
      if (!tile || window.matchMedia('(hover: none)').matches || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const video = tile.querySelector('.qls-social__media');
      if (!video) return;
      this.hydrate(video);
      video.muted = true;
      video.loop = true;
      video.play().then(() => tile.classList.add('is-previewing')).catch(() => {});
    }

    stopPreview(tile) {
      if (!tile) return;
      const video = tile.querySelector('.qls-social__media');
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
      tile.classList.remove('is-previewing');
    }

    onPointerOver(event) {
      const tile = event.target.closest('.qls-social__tile');
      if (tile && !tile.contains(event.relatedTarget)) this.preview(tile);
    }

    onPointerOut(event) {
      const tile = event.target.closest('.qls-social__tile');
      if (tile && !tile.contains(event.relatedTarget)) this.stopPreview(tile);
    }

    onFocusIn(event) {
      const tile = event.target.closest('.qls-social__tile');
      if (tile) this.preview(tile);
    }

    onClick(event) {
      const quickAdd = event.target.closest('[data-qls-quick-add]');
      if (quickAdd) {
        if (this.dialog.open) this.dialog.close();
        return;
      }
      const open = event.target.closest('[data-qls-open]');
      if (open) {
        this.open(Number(open.dataset.qlsOpen));
        return;
      }
      if (event.target === this.dialogVideo) {
        this.togglePlayback();
        return;
      }
      if (event.target.closest('[data-qls-close]')) this.dialog.close();
      if (event.target.closest('[data-qls-sound]')) this.toggleSound();
      if (event.target.closest('[data-qls-prev]')) this.open(this.activeIndex - 1);
      if (event.target.closest('[data-qls-next]')) this.open(this.activeIndex + 1);
      if (event.target.closest('[data-qls-rail-prev]')) this.scrollRail(-1);
      if (event.target.closest('[data-qls-rail-next]')) this.scrollRail(1);
      const viewCart = event.target.closest('[data-qls-view-cart]');
      if (viewCart) this.openCartDrawer(viewCart);
    }

    async onProductSubmit(event) {
      if (!event.target.matches('.qls-social__product .js-product-form')) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      const form = event.target;
      const button = form.querySelector('[name="add"]');
      if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;

      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-loading');
      const originalLabel = button.textContent.trim();

      try {
        const formData = new FormData(form);
        const drawer = document.querySelector('cart-drawer');
        const drawerSection = drawer?.closest('.shopify-section')?.id?.replace('shopify-section-', '') || 'cart-drawer';
        formData.append('sections', `cart-icon-bubble,${drawerSection}`);
        formData.append('sections_url', window.location.pathname);
        const response = await fetch(window.theme?.routes?.cartAdd || '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add this item.');

        await this.updateCartWithoutOpening(data, drawerSection);
        button.type = 'button';
        button.removeAttribute('name');
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading');
        button.classList.add('is-success');
        button.dataset.qlsViewCart = '';
        button.dataset.qlsOriginalLabel = originalLabel;
        button.textContent = 'View cart';
        this.addedProductForms.add(form);
      } catch (error) {
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading');
        const alert = form.closest('product-form')?.querySelector('[role="alert"]');
        if (alert) {
          alert.textContent = error.message;
          alert.hidden = false;
        }
      }
    }

    async updateCartWithoutOpening(data, drawerSection) {
      const iconSection = data.sections?.['cart-icon-bubble'];
      const iconTarget = document.getElementById('cart-icon-bubble');
      if (iconSection && iconTarget) {
        const parsed = new DOMParser().parseFromString(iconSection, 'text/html');
        const replacement = parsed.getElementById('cart-icon-bubble');
        if (replacement) iconTarget.innerHTML = replacement.innerHTML;
      }

      const drawer = document.querySelector('cart-drawer');
      const drawerHtml = data.sections?.[drawerSection];
      if (drawer && typeof drawer.renderContents === 'function') {
        await drawer.renderContents(data, false);
      } else if (drawer && drawerHtml) {
        const parsed = new DOMParser().parseFromString(drawerHtml, 'text/html');
        const replacement = parsed.querySelector('cart-drawer');
        if (replacement) drawer.innerHTML = replacement.innerHTML;
      }
      document.dispatchEvent(new CustomEvent('qls:cart-item-touched', {
        detail: { itemKey: data.key || '', variantId: data.variant_id, quantity: data.quantity, source: 'add' }
      }));
    }

    openCartDrawer(button) {
      if (this.dialog.open) this.dialog.close();
      requestAnimationFrame(() => {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open(button);
        else document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: button } }));
      });
    }

    scrollRail(direction) {
      const rail = this.querySelector('.qls-social__rail');
      const tile = rail?.querySelector('.qls-social__tile');
      if (!rail || !tile) return;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 16;
      rail.scrollBy({ left: direction * (tile.getBoundingClientRect().width + gap), behavior: 'smooth' });
    }

    open(index) {
      const slides = [...this.querySelectorAll('.qls-social__data')];
      if (!slides.length) return;
      this.activeIndex = (index + slides.length) % slides.length;
      this.render(JSON.parse(slides[this.activeIndex].textContent));
      if (!this.dialog.open) {
        this.lockPageScroll();
        this.dialog.showModal();
      }
    }

    render(data) {
      this.setAttribute('aria-busy', 'true');
      this.dialogVideo.pause();
      this.dialogVideo.innerHTML = '';
      this.dialogImage.src = data.poster || '';
      this.dialogImage.hidden = data.sources.length > 0;
      this.dialogVideo.hidden = data.sources.length === 0;
      this.querySelector('[data-qls-sound]').hidden = data.sources.length === 0;

      data.sources.forEach((item) => {
        const source = document.createElement('source');
        source.src = item.url;
        source.type = item.mime_type;
        this.dialogVideo.append(source);
      });

      if (data.sources.length) {
        this.dialogVideo.muted = this.preferredMuted;
        this.dialogVideo.loop = true;
        this.dialogVideo.load();
        this.dialogVideo.play().catch(() => {});
        this.updateSound();
      }

      const avatar = this.querySelector('[data-qls-avatar]');
      avatar.src = data.avatar || '';
      avatar.hidden = !data.avatar;
      this.querySelector('[data-qls-account]').textContent = data.account || '';
      const caption = this.querySelector('[data-qls-caption]');
      caption.textContent = data.caption || '';
      caption.closest('.qls-social__copy').hidden = !data.caption;

      const social = this.querySelector('[data-qls-social]');
      social.href = data.social_url || '#';
      social.hidden = !data.social_url;

      this.querySelectorAll('[data-qls-product]').forEach((product) => {
        product.hidden = !data.products.includes(product.dataset.qlsProduct);
      });
      const hasProducts = data.products.length > 0;
      this.querySelector('.qls-social__panel').classList.toggle('is-empty', !hasProducts);
      this.querySelector('.qls-social__dialog-shell').classList.toggle('is-story-only', !hasProducts);
      this.removeAttribute('aria-busy');
    }

    toggleSound() {
      this.dialogVideo.muted = !this.dialogVideo.muted;
      this.preferredMuted = this.dialogVideo.muted;
      this.updateSound();
    }

    togglePlayback() {
      if (this.dialogVideo.hidden || !this.dialogVideo.currentSrc) return;
      if (this.dialogVideo.paused) {
        this.dialogVideo.play().catch(() => {});
      } else {
        this.dialogVideo.pause();
      }
    }

    updateSound() {
      const button = this.querySelector('[data-qls-sound]');
      const muted = this.dialogVideo.muted;
      button.setAttribute('aria-label', muted ? button.dataset.unmuteLabel : button.dataset.muteLabel);
      button.querySelector('[data-qls-sound-icon]').textContent = muted ? '🔇' : '🔊';
    }

    stopDialog() {
      this.dialogVideo.pause();
      this.dialogVideo.innerHTML = '';
      this.dialogImage.src = '';
      this.preferredMuted = true;
      this.resetProductActions();
      this.unlockPageScroll();
    }

    lockPageScroll() {
      if (this.scrollLock) return;
      const scrollY = window.scrollY;
      const body = document.body;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      this.scrollLock = {
        scrollY,
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
        overflow: body.style.overflow,
        paddingRight: body.style.paddingRight
      };
      document.documentElement.classList.add('qls-social-modal-open');
      body.classList.add('qls-social-modal-open');
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;
    }

    unlockPageScroll() {
      if (!this.scrollLock) return;
      const body = document.body;
      const lock = this.scrollLock;
      document.documentElement.classList.remove('qls-social-modal-open');
      body.classList.remove('qls-social-modal-open');
      body.style.position = lock.position;
      body.style.top = lock.top;
      body.style.left = lock.left;
      body.style.right = lock.right;
      body.style.width = lock.width;
      body.style.overflow = lock.overflow;
      body.style.paddingRight = lock.paddingRight;
      this.scrollLock = null;
      window.scrollTo(0, lock.scrollY);
    }

    disconnectedCallback() {
      this.unlockPageScroll();
    }

    resetProductActions() {
      this.addedProductForms.forEach((form) => {
        const button = form.querySelector('[data-qls-view-cart]');
        if (!button) return;
        button.type = 'submit';
        button.name = 'add';
        button.textContent = button.dataset.qlsOriginalLabel || 'Add to cart';
        button.classList.remove('is-success');
        delete button.dataset.qlsViewCart;
      });
      this.addedProductForms.clear();
    }
  }

  customElements.define('qls-shoppable-video', QlsShoppableVideo);
}
